import { NavigationController, type NavPort } from "./NavigationController";
import type { RenderStrategy } from "./strategies/RenderStrategy";
import type { CameraState, PlaneOrientation, WorldPos } from "./types";

/** The session's window into study geometry — injected so the session is
 *  testable without Cornerstone/studyMetadata. In the app it's backed by
 *  canonical metadata + listAnatomicSign. */
export interface SessionGeometry {
  ippFor(listIndex: number): WorldPos | null;
  anatomicSign(): 1 | -1;
  totalSlices(): number;
  orientation(): PlaneOrientation;
  /** Whether index→nav should jump to a world position (the volume default) or
   *  go straight to showIndex. MPR reslice geometries return false: their
   *  rendered slices belong to the viewport's reoriented (coronal/sagittal)
   *  plane, which the axial IPP list cannot address — cornerstone owns that nav
   *  via jumpToSlice. Optional; absent ⇒ true (existing world-based behavior). */
  navigatesByWorld?(): boolean;
}

export interface ViewportSessionInit {
  slotId: string;
  seriesId: string;
  volumeId: string;
  geometry: SessionGeometry;
  onWarn?: (code: string, detail: Record<string, unknown>) => void;
}

export class ViewportSession {
  private strategy: RenderStrategy | null = null;
  private readonly nav: NavigationController;
  private sliceIndex = 0;
  private camera: CameraState = { zoom: 1, pan: [0, 0] };

  constructor(private readonly init: ViewportSessionInit) {
    const port: NavPort = {
      ippFor: (i) => this.init.geometry.ippFor(i),
      anatomicSign: () => this.init.geometry.anatomicSign(),
      totalSlices: () => this.init.geometry.totalSlices(),
      orientation: () => this.init.geometry.orientation(),
      // Only the GPU volume strategy jumps to world losslessly; a Cpu2d stack
      // (and the not-yet-attached state) navigates by list-index. A reslice
      // (MPR) geometry also opts out (navigatesByWorld=false) even on the GPU
      // path — its axial IPP list can't address the reoriented plane's slices.
      navigatesByWorld: () =>
        this.strategy?.kind === "GpuVolume" && (this.init.geometry.navigatesByWorld?.() ?? true),
      showWorld: (w, o) => this.strategy?.showWorld(w, o),
      showIndex: (i) => this.strategy?.showIndex(i),
      onWarn: (c, d) => this.init.onWarn?.(c, d),
    };
    this.nav = new NavigationController(port);
  }

  async attachStrategy(strategy: RenderStrategy, element: HTMLDivElement): Promise<void> {
    // Keep finding jumps pending until attach finishes; initial camera/slice
    // setup can otherwise overwrite a jump that was already marked consumed.
    this.strategy = null;
    await strategy.attach(element, this.init.volumeId);
    this.strategy = strategy;
    strategy.setCamera(this.camera);
    this.nav.navigateToIndex(this.sliceIndex);
  }

  /** Lossless swap: attach the new strategy, replay current state, dispose old. */
  async swapStrategy(next: RenderStrategy, element: HTMLDivElement): Promise<void> {
    const prev = this.strategy;
    await this.attachStrategy(next, element);
    prev?.dispose();
  }

  setSlice(listIndex: number): void {
    this.sliceIndex = listIndex;
    this.nav.navigateToIndex(listIndex);
  }

  setCamera(camera: CameraState): void {
    this.camera = camera;
    this.strategy?.setCamera(camera);
  }

  /** Update the in-memory camera snapshot WITHOUT re-applying it to the strategy.
   *  The CORNERSTONE_CAMERA_MODIFIED mirror calls this so context-loss recover()
   *  / re-attach replays the user's latest zoom/pan (attachStrategy replays
   *  this.camera) — while avoiding the re-apply feedback loop that setCamera()
   *  (which forwards to the strategy → setZoom/setPan → render) would risk when
   *  fed the value the viewport just reported. */
  recordCamera(camera: CameraState): void {
    this.camera = camera;
  }

  /** Cross-viewport AI jump: navigate to a world point directly, WITHOUT
   *  resolving a list-index (jumpToWorld is order/orientation-agnostic — the
   *  reason the AI-jump channel is world-space, not index-space). Returns false
   *  when no strategy is attached yet (volume still binding) so the caller can
   *  defer to its bind-site sync (first-frame gate). Mirrors VolumeViewport's
   *  `csVp.jumpToWorld(ipp)` call; the strategy self-guards a missing viewport. */
  showWorldNow(world: WorldPos): boolean {
    if (!this.strategy) return false;
    this.strategy.showWorld(world, this.init.geometry.orientation());
    return true;
  }
}
